import { useEffect, useState } from 'react'
import { Dialog } from './Dialog'
import { StarHistory } from './StarHistory'
import { starWhen } from './RecognitionCard'
import { hubLangFor, type HubLang } from '../lib/i18n'
import { STARS } from '../lib/hubCopy'
import {
  loadStarEarnings, loadStarSeries, STAR_RANGES,
  type StarBucket, type StarCredit, type StarRange,
} from '../lib/stars'
import { onStarsEarned } from '../lib/starPulse'

/**
 * "Stars you earned" — the feed under both balance cards and the modal behind
 * it. REC-57, migration 106 (David, 24 Sep).
 *
 * ── ONE ROW SHAPE, TWO CARDS ────────────────────────────────────────────────
 *
 * The hub's "Stars you received" card and #/stars "Your stars" both show the
 * last three credits and both open the same modal. One component, so the two
 * cannot describe one credit two ways. On the hub card this REPLACES the three
 * most recent recognitions it used to list: a recognition is one kind of
 * credit, and two lists on one card — "recognized you" above "you earned"
 * — would be two answers to "where did my stars come from".
 *
 * ── A RECOGNITION NAMES THE GIVER; ACTIVITY NAMES NOBODY ────────────────────
 *
 * "Marta recognized you", never your own name back at you (072). Activity is
 * described by what you did, from copy keyed on the catalogue's rule key; a
 * key this build has no words for reads "Taking part in OneConetec" rather
 * than a raw key — the reader is an employee, not the admin screen.
 *
 * ── A SHOUT-OUT SHOWS NO FIGURE; A REVERSAL SHOWS BOTH ──────────────────────
 *
 * A zero-star recognition is a shout-out (061), and "+0" reads as a gift of
 * nothing, so it carries no amount. An activity whose post was removed shows
 * what is left AND what was taken back (099) — the row stays, because it was
 * earned and seen (rule 10), and a bare "+0" would look like a bug.
 *
 * ── THE PILLS MOVE THE CHART, NOT THE LIST ──────────────────────────────────
 *
 * The list under the chart is everything, newest first, a page at a time. A
 * list narrowed by range would need the range in the DATABASE call (standing
 * rule 1 — filtering a fetched page returns "some of them"), and the list is
 * already in date order, so the recent ones are the ones on top anyway.
 * `.chip` for the pills: it is the app's single "pick one of these" control
 * (the Wiki One sort is the precedent), never a second segmented vocabulary.
 */

export type CreditWords = {
  who: string
  /** What it was for (128) or nothing, then the coarse age. */
  meta: string
  /** "+40", or null for a shout-out. */
  amount: string | null
  /** "5 taken back when it was removed", or null. */
  note: string | null
}

export function creditWords(c: StarCredit, lang: HubLang): CreditWords {
  const t = STARS[lang]
  const when = starWhen(c.creditedAt, lang)
  if (c.source === 'recognition') {
    return {
      who: c.giverName ? t.recognizedYou(c.giverName) : t.someoneRecognizedYou,
      meta: c.endorsements.length ? `${c.endorsements.map((e) => e.label).join(', ')} · ${when}` : when,
      amount: c.stars > 0 ? `+${c.stars.toLocaleString()}` : null,
      note: null,
    }
  }
  return {
    who: (c.ruleKey && t.activity[c.ruleKey]) || t.activityOther,
    meta: when,
    amount: c.stars > 0 || c.reversedStars > 0 ? `+${c.stars.toLocaleString()}` : null,
    note: c.reversedStars > 0 ? t.takenBack(c.reversedStars) : null,
  }
}

export function StarCreditList({ credits, locale = 'en-CA' }: { credits: StarCredit[]; locale?: string }) {
  const lang = hubLangFor(locale)
  return (
    <ul className="earned">
      {credits.map((c) => {
        const w = creditWords(c, lang)
        return (
          <li className="earned__row" key={c.creditId}>
            <div className="earned__text">
              <p className="earned__who">{w.who}</p>
              <p className="earned__meta">{w.meta}</p>
              {w.note && <p className="earned__meta earned__note">{w.note}</p>}
            </div>
            {w.amount && <p className="earned__amount">{w.amount} <span aria-hidden="true">★</span></p>}
          </li>
        )
      })}
    </ul>
  )
}

/**
 * The modal's content as props, so the suite can render every state of it
 * (an effect never runs under `renderToString`). The loader below owns the
 * fetching and nothing else.
 */
export function StarEarningsView({
  range,
  onRange,
  series,
  credits,
  total,
  loadingMore = false,
  error = null,
  onMore,
  locale = 'en-CA',
}: {
  range: StarRange
  onRange: (r: StarRange) => void
  /** null while the chosen range is loading. */
  series: StarBucket[] | null
  /** null while the first page is loading. */
  credits: StarCredit[] | null
  total: number
  loadingMore?: boolean
  error?: string | null
  onMore: () => void
  locale?: string
}) {
  const t = STARS[hubLangFor(locale)]
  return (
    <div className="earnedview">
      <div className="chips" role="group" aria-label={t.rangePicker}>
        {STAR_RANGES.map((r) => (
          <button
            key={r}
            type="button"
            className={`chip${r === range ? ' chip--on' : ''}`}
            aria-pressed={r === range}
            onClick={() => onRange(r)}
          >
            {t.rangeName[r]}
          </button>
        ))}
      </div>

      {error && <p className="earnedview__error" role="alert">{error}</p>}

      {series === null ? (
        <p className="empty">{t.loadingEarned}</p>
      ) : (
        <StarHistory buckets={series} range={range} locale={locale} tall sayWhenEmpty />
      )}

      <h3 className="earnedview__head">{t.everythingEarned}</h3>
      {credits === null ? (
        <p className="empty">{t.loadingEarned}</p>
      ) : credits.length === 0 ? (
        <p className="empty">{t.noneEarnedYet}</p>
      ) : (
        <>
          <StarCreditList credits={credits} locale={locale} />
          {/* A page says it is one (standing rule 1). The count is every
              credit, from the same statement as the rows. */}
          <p className="earnedview__foot">
            {t.shownOf(credits.length, Math.max(total, credits.length))}
          </p>
          {credits.length < total && (
            <button type="button" className="btn btn--quiet btn--sm" onClick={onMore} disabled={loadingMore}>
              {loadingMore ? t.loadingEarned : t.showMore}
            </button>
          )}
        </>
      )}
    </div>
  )
}

const PAGE = 20

export function StarEarningsDialog({
  open,
  onClose,
  locale = 'en-CA',
}: {
  open: boolean
  onClose: () => void
  locale?: string
}) {
  const t = STARS[hubLangFor(locale)]
  const [range, setRange] = useState<StarRange>('12m')
  const [series, setSeries] = useState<StarBucket[] | null>(null)
  const [credits, setCredits] = useState<StarCredit[] | null>(null)
  const [total, setTotal] = useState(0)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // The chart, per range. `live` guards a fast click through the pills: the
  // 1m reply arriving after the all-time one must not overwrite it.
  useEffect(() => {
    if (!open) return
    let live = true
    setSeries(null)
    loadStarSeries(range)
      .then((s) => live && setSeries(s))
      .catch(() => {
        if (!live) return
        setSeries([])
        setError(t.couldNotLoadEarned)
      })
    return () => {
      live = false
    }
  }, [open, range, t])

  // The first page, once per opening — and again when the pill fires, so a
  // dialog left open while a comment lands does not describe the past.
  useEffect(() => {
    if (!open) return
    let live = true
    const first = () =>
      loadStarEarnings({ limit: PAGE })
        .then((p) => {
          if (!live) return
          setCredits(p.credits)
          setTotal(p.total)
        })
        .catch(() => {
          if (!live) return
          setCredits([])
          setError(t.couldNotLoadEarned)
        })
    setError(null)
    setCredits(null)
    first()
    const off = onStarsEarned(() => {
      first()
      loadStarSeries(range).then((s) => live && setSeries(s)).catch(() => {})
    })
    return () => {
      live = false
      off()
    }
    // `range` deliberately not a dependency: changing the pill must not refetch the list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, t])

  function more() {
    if (!credits || credits.length === 0) return
    setLoadingMore(true)
    loadStarEarnings({ limit: PAGE, after: credits[credits.length - 1] })
      .then((p) => setCredits((prev) => [...(prev ?? []), ...p.credits]))
      .catch(() => setError(t.couldNotLoadEarned))
      .finally(() => setLoadingMore(false))
  }

  return (
    <Dialog open={open} title={t.earnedDialog} onClose={onClose} className="earnedpanel">
      <StarEarningsView
        range={range}
        onRange={setRange}
        series={series}
        credits={credits}
        total={total}
        loadingMore={loadingMore}
        error={error}
        onMore={more}
        locale={locale}
      />
    </Dialog>
  )
}

/**
 * What both balance cards render under their numbers: the heading, the last
 * three, and the button that opens the modal. The button is a button, not a
 * link — there is no screen behind it, only the dialog.
 */
export function RecentlyEarned({
  credits,
  locale = 'en-CA',
  empty,
}: {
  credits: StarCredit[]
  locale?: string
  /** The card's own empty-state sentence. */
  empty: string
}) {
  const t = STARS[hubLangFor(locale)]
  const [open, setOpen] = useState(false)
  return (
    <div className="recentearned">
      {credits.length === 0 ? (
        <p className="empty">{empty}</p>
      ) : (
        <>
          <p className="recentearned__head">{t.earnedHeading}</p>
          <StarCreditList credits={credits.slice(0, 3)} locale={locale} />
        </>
      )}
      <button type="button" className="btn btn--quiet btn--sm" onClick={() => setOpen(true)}>
        {t.seeAllEarned}
      </button>
      <StarEarningsDialog open={open} onClose={() => setOpen(false)} locale={locale} />
    </div>
  )
}
